import { get, omit } from 'lodash/object';
import queryString from 'query-string';
import { consts as pumaConsts } from '@x-tech/fe-puma-lib';
import * as authHelper from '@/helper/auth';
import * as authApi from '@/api/auth';
import * as ssoApi from '@/api/sso';
import * as commonApi from '@/api/index';
import * as consts from '@/components/SecondAuth/consts';
import { IS_LOCAL, LOCAL_PRODUCT } from '@/config';
import {
    isStartsWithHttp,
    isStartsWithSlashContainer,
    isSFUrl,
    isIMSUrl,
    genCommonRedirectURL,
    genImsJumpUrl,
    getAppCodeFromRedirect,
    cleanUrl,
} from './helper';
import store from '@/store/store';
import {$_initWs} from '@/ws';


function getRedirectQuery(query = {}) {
    try {
        return queryString.stringify(omit(query, 'redirect'));
    } catch (e) {
        console.error(e);
        return '';
    }
}

const loginSuccess = {
    methods: {
        async $_loginSuccess(res) {
            cleanUrl();

            // 强制修改密码
            if (!res.secure) {
                this.$message.warning(res.msg);
                await this.$router.push({ path: `/forgotPassword/${this.loginForm['username']}?isModifyPwd=1` });
                return;
            }

            this.$message.success('登录成功');

            this.$_setCookie(res);
            if (!await this.$_initData()) {
                return;
            }

            // 在跳转页面之前 初始化 ws
            $_initWs();
            try {
                this.$log(
                    `loginUser:${
                        this.$store.state.user.username ? this.$store.state.user.username : this.$store.state.user.id
                    }`
                );
                // 这里捕获异常之后，确实无需特殊操作
                // eslint-disable-next-line no-empty
            } catch (e) {}
            const redirect = this.$route.query.redirect;

            const messageId = this.$route.query['message_id'];
            if (messageId) {
                try {
                    await commonApi.msgClick({ id: messageId });
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }

            // 是否需要二次验证
            if (await this.$_handleSecondAuth(redirect)) {
                return;
            }

            // 是否需要/允许跳转
            if (await this.$_handleRedirect(redirect, res.accessToken, res.preAuthCode)) {
                return;
            }
            await this.$_normalLoginSuccessFlow();
        },
        // 尝试根据 redirect 获取产品信息
        async $_getRedirectProducts(redirect = '') {
            let products;
            if (isStartsWithHttp(redirect)) {
                // 通过 URL 获取产品信息
                try {
                    products = await authApi.getProductInfoWithUrl(redirect);
                } catch (e) {
                    console.error(e);
                    products = [];
                }
            } else if (isStartsWithSlashContainer(redirect)) {
                // 如果是 /container 开头的，则需要在 hash 中直接获取相应的产品
                const appCode = getAppCodeFromRedirect(redirect);
                const product = get(store.state.productsCanAccessHash, appCode, undefined);
                if (product) {
                    products = [product];
                } else {
                    products = [];
                }
            } else {
                // 跳转本地的非 /container 页面
                products = [];
            }
            return products;
        },
        // 判断是否需要进行二次验证
        // 如果需要直接跳转到 /welcome
        async $_handleSecondAuth(redirect = '') {
            let products = await this.$_getRedirectProducts(redirect);

            // 没有匹配到产品的 直接放过
            if (products.length === 0) {
                return false;
            } else {
                // 虽然 redirect 有可能多个产品，但这里只取第一个
                const product = products[0];
                if (product.secondAuth && product.secondAuth !== consts.SECOND_AUTH_NONE) {
                    const user = store.state.user;
                    if (user.passSecondAuth.indexOf(product.secondAuth) === -1) {
                        this.$message.warning('请先进行二次验证');
                        await this.$router.push({
                            name: 'welcome',
                        });
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return false;
                }
            }
        },
        // 如果被成功处理，返回 true，否则返回 false
        // @TODO 这里处理失败时，使用异常似乎更加合理
        async $_handleRedirect(redirect = '', accessToken = '', preAuthCode = '') {
            if (redirect === '' || accessToken === '') {
                return false;
            }

            // 判断当前用户是否有权限跳转指定的路径对应的项目
            let products = await this.$_getRedirectProducts(redirect);
            if (products.length > 0) {
                let { productCode, productName } = products[0];
                if (Object.keys(store.state.productsCanAccessHash).indexOf(productCode) === -1) {
                    let msg;
                    if (productName) {
                        msg = `无 '${productName}' 访问权限`;
                    } else {
                        msg = '无访问权限';
                    }
                    this.$message.warning(msg);
                    await this.$router.push({ name: 'welcome' });
                    return false;
                }
            }

            // puma 作为 sso
            if (isStartsWithHttp(redirect)) {
                if (isIMSUrl(redirect)) {
                    // ims 登录需要获取一个特殊的参数: ticket
                    // ims 登录会存在一个 service 参数，需要保留 encode 状态
                    // 但是 this.$route.query.redirect 会无脑进行 decode 处理
                    let res;
                    try {
                        res = await ssoApi.getTicket(accessToken, window.XCConfig.VUE_APP_OA_PLATFORM_ID);
                    } catch (e) {
                        console.error(e);
                        return false;
                    }

                    let _redirect;
                    try {
                        _redirect = genImsJumpUrl(redirect, res.ticket, accessToken);
                    } catch (e) {
                        console.error(e);
                        return false;
                    }
                    window.location.href = _redirect;
                    return true;
                } else {
                    let redirectUrl;
                    try {
                        redirectUrl = genCommonRedirectURL(redirect, accessToken, preAuthCode);
                    } catch (e) {
                        this.$message.error('跳转业务系统失败，请稍后重试');
                        console.error(e);
                        return false;
                    }
                    window.location.href = redirectUrl;
                    return true;
                }
            } else if (redirect === '/container') {
                // 只有 '/container' 的特殊情况（子应用的路由信息没有同步等情况，直接无视）
                await this.$_normalLoginSuccessFlow();
                return true;
            } else if (isSFUrl(redirect)) {
                let redirectSFUrl;
                try {
                    redirectSFUrl = await authApi.getSfUrl();
                } catch (e) {
                    this.$message.error('跳转业务系统失败，请稍后重试');
                    console.error(e);
                    return false;
                }
                window.location.href = redirectSFUrl;
                return true;
            } else {
                const query = getRedirectQuery(this.$route.query);
                let hash;
                if (query) {
                    hash = `${redirect}?${query}`;
                } else {
                    hash = redirect;
                }
                window.location.hash = hash;
                return true;
            }
        },
        async $_initData() {
            let user;
            try {
                user = await authApi.getUserInfo();
            } catch (e) {
                console.error(e);
                this.$message.error('获取用户信息失败，请稍后重新登录');
                return false;
            }

            let products;
            try {
                products = await authApi.getProductsCanAccess(user.id);
            } catch (e) {
                console.error(e);
                this.$message.error('获取用户可访问产品失败，请稍后重新登录');
                return false;
            }
            if (IS_LOCAL) {
                if (Object.prototype.toString.call(LOCAL_PRODUCT) === '[object Array]') {
                    products = LOCAL_PRODUCT.concat(products);
                } else {
                    products.unshift(LOCAL_PRODUCT);
                }
            }
            await this.$store.dispatch('initData', { user, products });
            return true;
        },
        async $_normalLoginSuccessFlow() {
            await this.$router.push({
                name: 'welcome',
            });
        },
        $_setCookie(payload) {
            const domain = authHelper.get2LD();
            this.$cookie.set(pumaConsts.TOKEN_COOKIE_NAME, JSON.stringify(payload), {
                domain,
                expires: '',
            });
        },
    },
};

export default loginSuccess;
